import { LeftOutlined, QuestionCircleOutlined, RightOutlined } from "@ant-design/icons";
import { Button, Card, Col, Modal, Row, Segmented, Select, Tooltip } from "antd";
import ReactECharts from "echarts-for-react";
import { revenueApi } from "api/revenue.api";
import { AppButton } from "components/antd-ui";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  RevenueReconRow,
  RevenueMonthlyPoint,
  RevenuePreset,
  revenuePresetOptions,
  RevenueSummary,
  RevenueTopCustomer,
} from "types/revenue";
import { formatVND, getTitle } from "utils";
import { barValueLabel } from "utils/chart";
import dayjs, { Dayjs } from "utils/dayjs";
import { ReconTable, ReconTotals } from "./components/ReconTable";

const formatMoney = (num: number) => `${formatVND(Math.round(num || 0))}đ`;

const formatAxisMoney = (value: number) => {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) {
    const rounded = Math.round((value / 1_000_000_000) * 10) / 10;
    const sign = rounded < 0 ? "-" : "";
    const whole = Math.trunc(Math.abs(rounded));
    const decimal = Math.round((Math.abs(rounded) - whole) * 10);
    const body = formatVND(whole);
    return decimal === 0 ? `${sign}${body} tỷ` : `${sign}${body},${decimal} tỷ`;
  }
  if (abs >= 1_000_000) {
    return `${formatVND(Math.round(value / 1_000_000))} tr`;
  }
  return formatVND(value);
};

const displayDate = (value?: string) => (value ? dayjs(value).format("DD/MM/YYYY") : "--");

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, index) => CURRENT_YEAR - index);

const buildTopCustomersOption = (rows: RevenueTopCustomer[]) => {
  const sorted = [...rows].reverse(); // echarts horizontal bar vẽ từ dưới lên
  return {
    grid: { left: 180, right: 72, top: 16, bottom: 28 },
    tooltip: { trigger: "axis", valueFormatter: (value: number) => formatMoney(Number(value)) },
    xAxis: {
      type: "value",
      axisLabel: {
        hideOverlap: true,
        formatter: (value: number) => formatAxisMoney(value),
      },
    },
    yAxis: {
      type: "category",
      data: sorted.map((row) => row.customerName),
      axisLabel: { width: 140, overflow: "truncate" },
    },
    series: [
      {
        type: "bar",
        data: sorted.map((row) => row.revenue),
        itemStyle: { color: "#f58a07", borderRadius: [0, 4, 4, 0] },
        barMaxWidth: 20,
        label: barValueLabel("right"),
      },
    ],
  };
};

// 1 khách hàng cần khoảng 28px cao để không bị chèn chữ/số, tối thiểu 360px
const topCustomersChartHeight = (rows: RevenueTopCustomer[]) => Math.max(360, rows.length * 28 + 60);

export const RevenuePage = observer(({ title = "" }) => {
  const [preset, setPreset] = useState<RevenuePreset>("THIS_YEAR");
  const [summary, setSummary] = useState<RevenueSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [year, setYear] = useState<number>(CURRENT_YEAR);
  const [monthlyRows, setMonthlyRows] = useState<RevenueMonthlyPoint[]>([]);
  const [topCustomers, setTopCustomers] = useState<RevenueTopCustomer[]>([]);
  const [chartLoading, setChartLoading] = useState(false);

  const [reconOpen, setReconOpen] = useState(false);
  const [reconTab, setReconTab] = useState("ALL");
  const [reconRows, setReconRows] = useState<RevenueReconRow[]>([]);
  const [reconTotals, setReconTotals] = useState<ReconTotals>({ expected: 0, actual: 0, difference: 0 });
  const [reconLoading, setReconLoading] = useState(false);

  const [monthValue, setMonthValue] = useState<Dayjs>(dayjs().startOf("month"));
  const [monthRows, setMonthRows] = useState<RevenueReconRow[]>([]);
  const [monthTotals, setMonthTotals] = useState<ReconTotals>({ expected: 0, actual: 0, difference: 0 });
  const [monthLoading, setMonthLoading] = useState(false);

  const [topCustomersAllTime, setTopCustomersAllTime] = useState<RevenueTopCustomer[]>([]);
  const [allTimeLoading, setAllTimeLoading] = useState(false);

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  const fetchSummary = useCallback(async (p: RevenuePreset) => {
    setSummaryLoading(true);
    try {
      const res = await revenueApi.summary(p);
      setSummary(res.data);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary(preset);
  }, [fetchSummary, preset]);

  const fetchCharts = useCallback(async (y: number) => {
    setChartLoading(true);
    try {
      const [monthlyRes, topRes] = await Promise.all([revenueApi.monthlyChart(y), revenueApi.topCustomersChart(y)]);
      setMonthlyRows(monthlyRes.data.rows || []);
      setTopCustomers(topRes.data.rows || []);
    } finally {
      setChartLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCharts(year);
  }, [fetchCharts, year]);

  useEffect(() => {
    const fetchAllTime = async () => {
      setAllTimeLoading(true);
      try {
        const res = await revenueApi.topCustomersAllTimeChart();
        setTopCustomersAllTime(res.data.rows || []);
      } finally {
        setAllTimeLoading(false);
      }
    };
    fetchAllTime();
  }, []);

  const openRecon = useCallback(
    async (tab = "ALL") => {
      setReconTab(tab);
      setReconOpen(true);
      setReconRows([]);
      setReconLoading(true);
      try {
        const res = await revenueApi.summaryDetail({ preset });
        setReconRows(res.data.rows || []);
        setReconTotals({
          expected: res.data.expected || 0,
          actual: res.data.actual || 0,
          difference: res.data.difference || 0,
        });
      } finally {
        setReconLoading(false);
      }
    },
    [preset],
  );

  const fetchMonth = useCallback(async () => {
    setMonthLoading(true);
    try {
      const res = await revenueApi.summaryDetail({ month: monthValue.month() + 1, year: monthValue.year() });
      setMonthRows(res.data.rows || []);
      setMonthTotals({
        expected: res.data.expected || 0,
        actual: res.data.actual || 0,
        difference: res.data.difference || 0,
      });
    } finally {
      setMonthLoading(false);
    }
  }, [monthValue]);

  useEffect(() => {
    fetchMonth();
  }, [fetchMonth]);

  const monthNav = (
    <div className="flex items-center gap-1">
      <AppButton tone="light" aria-label="Tháng trước" onClick={() => setMonthValue((prev) => prev.subtract(1, "month"))}>
        <LeftOutlined />
      </AppButton>
      <div className="min-w-[90px] text-center font-semibold">{monthValue.format("MM/YYYY")}</div>
      <AppButton tone="light" aria-label="Tháng sau" onClick={() => setMonthValue((prev) => prev.add(1, "month"))}>
        <RightOutlined />
      </AppButton>
    </div>
  );

  const difference = summary?.difference ?? 0;

  const rangeLabel = summary ? `${dayjs(summary.from).format("DD/MM/YYYY")} — ${dayjs(summary.to).format("DD/MM/YYYY")}` : "";

  const monthlyChartOption = useMemo(
    () => ({
      grid: { left: 110, right: 24, top: 32, bottom: 32 },
      tooltip: { trigger: "axis", valueFormatter: (value: number) => formatMoney(Number(value)) },
      xAxis: {
        type: "category",
        data: monthlyRows.map((row) => `Tháng ${row.month}`),
      },
      yAxis: {
        type: "value",
        axisLabel: { formatter: (value: number) => formatVND(value) },
      },
      series: [
        {
          type: "bar",
          data: monthlyRows.map((row) => row.revenue),
          itemStyle: { color: "#1fa6aa", borderRadius: [4, 4, 0, 0] },
          barMaxWidth: 36,
          label: barValueLabel("top"),
        },
      ],
    }),
    [monthlyRows],
  );

  const topCustomersChartOption = useMemo(() => buildTopCustomersOption(topCustomers), [topCustomers]);
  const topCustomersAllTimeOption = useMemo(() => buildTopCustomersOption(topCustomersAllTime), [topCustomersAllTime]);

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="font-semibold">Tổng quan</div>
          <div className="flex items-center gap-3 flex-wrap">
            {rangeLabel && <div className="text-base font-semibold text-gray-600">{rangeLabel}</div>}
            <Segmented
              value={preset}
              onChange={(value) => setPreset(value as RevenuePreset)}
              options={revenuePresetOptions.map((item) => ({ label: item.label, value: item.value }))}
            />
          </div>
        </div>

        <Row gutter={16} className="mt-4">
          <Col span={6}>
            <div className="text-xs text-gray-400 font-semibold uppercase">Tổng số lần thu</div>
            <div className="text-xl font-bold mt-1">{summary?.collectCount ?? 0}</div>
            <div className="text-xs text-gray-400 mt-1">Số đợt công nợ đã thu trong kỳ.</div>
            <Button type="link" size="small" className="!px-0" onClick={() => openRecon()}>
              Xem danh sách
            </Button>
          </Col>
          <Col span={6}>
            <div className="text-xs text-gray-400 font-semibold uppercase">Doanh thu thực tế</div>
            <div className="text-xl font-bold mt-1 text-teal-700">{formatMoney(summary?.actualRevenue || 0)}</div>
            <div className="text-xs text-gray-400 mt-1">Công nợ ĐÃ THU có ngày thu trong kỳ (gồm VAT).</div>
            <Button type="link" size="small" className="!px-0" onClick={() => openRecon()}>
              Xem danh sách
            </Button>
          </Col>
          <Col span={6}>
            <div className="text-xs text-gray-400 font-semibold uppercase">Doanh thu dự kiến</div>
            <div className="text-xl font-bold mt-1">{formatMoney(summary?.expectedRevenue || 0)}</div>
            <div className="text-xs text-gray-400 mt-1">Mọi công nợ có HẠN THU trong kỳ, kể cả đã thu (gồm VAT).</div>
            <Button type="link" size="small" className="!px-0" onClick={() => openRecon()}>
              Xem danh sách
            </Button>
          </Col>
          <Col span={6}>
            <div className="text-xs text-gray-400 font-semibold uppercase flex items-center gap-1">
              Chênh lệch
              <Tooltip title="Doanh thu thực tế = công nợ đã thu trong kỳ (gồm VAT). Doanh thu dự kiến = mọi công nợ có hạn thu trong kỳ (kể cả đã thu). Chênh lệch = Thực tế − Dự kiến (âm nghĩa là có công nợ đến hạn chưa thu được trong kỳ).">
                <QuestionCircleOutlined className="text-gray-400 cursor-help" />
              </Tooltip>
            </div>
            <div className={`text-xl font-bold mt-1 ${difference < 0 ? "text-red-600" : "text-green-600"}`}>
              {difference > 0 ? "+" : ""}
              {formatMoney(difference)}
            </div>
            <div className="text-xs text-gray-400 mt-1">Thực tế − Dự kiến.</div>
            <Button type="link" size="small" className="!px-0" onClick={() => openRecon("SHORT")}>
              Xem công nợ gây lệch
            </Button>
          </Col>
        </Row>
      </Card>

      <div>
        <div className="font-semibold mb-2">Danh sách công nợ theo tháng</div>
        <ReconTable rows={monthRows} totals={monthTotals} loading={monthLoading} leftActions={monthNav} />
      </div>

      <Card>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
          <div className="font-semibold">Biểu đồ doanh thu</div>
          <Select value={year} onChange={setYear} options={YEAR_OPTIONS.map((y) => ({ value: y, label: `Năm ${y}` }))} style={{ width: 140 }} />
        </div>

        <div className="text-sm font-medium mb-2">Doanh thu theo tháng</div>
        <ReactECharts
          key={chartLoading ? "monthly-loading" : `monthly-${monthlyRows.length}-${year}`}
          option={monthlyChartOption}
          style={{ height: 360 }}
          showLoading={chartLoading}
        />

        <Row gutter={16} className="mt-6">
          <Col span={12}>
            <div className="text-sm font-medium mb-2">Top 20 khách hàng theo doanh thu</div>
            <ReactECharts
              // Ép ECharts dựng lại khi dữ liệu thật đã có — tránh đo sai kích thước khung lúc layout
              // chưa ổn định (2 biểu đồ top khách hàng tải API lệch thời điểm nhau), gây chồng chữ trục số.
              key={chartLoading ? "top-year-loading" : `top-year-${topCustomers.length}-${year}`}
              option={topCustomersChartOption}
              style={{ height: topCustomersChartHeight(topCustomers) }}
              showLoading={chartLoading}
            />
          </Col>
          <Col span={12}>
            <div className="text-sm font-medium mb-2">Top 20 khách hàng theo doanh thu (tất cả thời gian)</div>
            <ReactECharts
              key={allTimeLoading ? "top-all-time-loading" : `top-all-time-${topCustomersAllTime.length}`}
              option={topCustomersAllTimeOption}
              style={{ height: topCustomersChartHeight(topCustomersAllTime) }}
              showLoading={allTimeLoading}
            />
          </Col>
        </Row>
      </Card>

      <Modal
        open={reconOpen}
        title={`Đối chiếu công nợ: dự kiến / thực tế${rangeLabel ? ` (${rangeLabel})` : ""}`}
        width={1180}
        footer={null}
        onCancel={() => setReconOpen(false)}
        destroyOnClose
      >
        <ReconTable rows={reconRows} totals={reconTotals} loading={reconLoading} initialTab={reconTab} />
      </Modal>
    </div>
  );
});
